import React from 'react';
import styled from 'styled-components';
import Tippy from '@tippyjs/react';
import { FormattedMessage } from 'react-intl';
import { useLazyQuery } from '@apollo/client';
import { Badge, DisplayText, ProgressBar } from '@superops/ui-design-system';
import { STATUS_MAP } from '@nile/projects/src/Constants';
import { Loader, DisabledTag } from '@nile/components';
import { GET_PROJECT } from '@nile/projects/src/Graphql';
import { useCheckIsItProduct } from '@nile/components/src/Utils/helpers';
import { getStatusTranslationKeys } from '@nile/projects/src/Pebbles/ProjectListTableContainer';

const LoaderContainer = styled.div`
  width: 200px;
  height: 100px;
`;

const InfoPopupCardContainer = styled.div`
  padding: 16px;
  min-width: 320px;
  max-width: 420px;
  max-height: 400px;
  overflow: auto;
  position: relative;
  line-break: anywhere;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NodescriptionContainer = styled.div`
  display: flex;
  padding: 8px 12px;
`;

const ProgressContainer = styled.div`
  background: #f4f5f7;
  border-radius: 8px;
  padding: 10px;
`;

function MilestoneCell(props) {
  const { cell } = props;
  const data = cell.row.original || cell.row.values || {};
  const { totalMilestones = 0, completedMilestones = 0 } = data;

  const isItProduct = useCheckIsItProduct();

  const calculateProgress = (completed: number, total: number): number => {
    if (!total) {
      return 0;
    }
    return Math.floor((completed / total) * 100);
  };

  const [
    getProject,
    { loading: projectLoading, data: projectData }
  ] = useLazyQuery(GET_PROJECT);
  const milestonesList = projectData?.getProject?.milestones || [];
  const statusOptions = getStatusTranslationKeys();

  return (
    <Tippy
      content={
        projectLoading ? (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        ) : !milestonesList.length ? (
          <NodescriptionContainer className="white-to-grey-bg">
            <FormattedMessage id="ticketlist.popup.nodescription.title" />
          </NodescriptionContainer>
        ) : (
          <InfoPopupCardContainer className="white-to-grey-bg">
            <div className="p12">
              <div className="mb30">
                <DisplayText size="large" weight="Bold">
                  <FormattedMessage id="admin.integrations.webroot.summary.overallstatus" />
                </DisplayText>
                <div className="flex wrap g8 mt20">
                  {Object.entries(statusOptions).map(([status, messageId]) => {
                    const count = milestonesList.filter(
                      (m: { status: string }) => m.status === status
                    ).length;
                    if (!count) return null;

                    return (
                      <React.Fragment key={status}>
                        {count}
                        <Badge status={STATUS_MAP[status]}>
                          <FormattedMessage id={messageId} />
                        </Badge>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              <>
                <div className="mb20">
                  <DisplayText size="large" weight="Bold">
                    <FormattedMessage id="attribute.totalMilestones" />
                  </DisplayText>
                </div>
                {milestonesList.map((milestone) => {
                  const remainingTasks =
                    milestone?.totalTasks - milestone?.completedTasks;
                  return (
                    <div
                      className="flex g8 col mb20"
                      key={milestone?.milestoneId}
                    >
                      <div className="flex g10 mb10">
                        <div className="ellipsis" title={milestone?.name}>
                          {milestone?.name}
                        </div>
                        <Badge status={STATUS_MAP[milestone?.status] || 'info'}>
                          <div className="nowrap">{milestone?.status}</div>
                        </Badge>
                      </div>
                      <ProgressContainer>
                        <div className="flex g4 space-between mb-10">
                          <div className="flex row g5">
                            {remainingTasks}
                            <div className="mr-5">
                              <FormattedMessage id="client.details.tktSummary.open" />
                            </div>
                          </div>
                          <div className="flex frow g5">
                            {milestone?.completedTasks}
                            <div className="mr-5">
                              <FormattedMessage id="app.common.completed" />
                            </div>
                          </div>
                          {milestone?.totalTasks ? (
                            <div className="ml8 font-weight-500">
                              {`${milestone?.completedTasks}/${milestone?.totalTasks}`}
                            </div>
                          ) : (
                            <DisabledTag>NA</DisabledTag>
                          )}
                        </div>
                        <ProgressBar
                          progress={calculateProgress(
                            milestone?.completedTasks,
                            milestone?.totalTasks
                          )}
                          size="small"
                          customColor="#009f36"
                        />
                      </ProgressContainer>
                    </div>
                  );
                })}
              </>
            </div>
          </InfoPopupCardContainer>
        )
      }
      maxWidth={400}
      maxHeight={500}
      delay={100}
      duration={250}
      interactive
      placement="bottom-start"
      arrow={false}
      theme="light"
      offset={[0, 5]}
      onShow={() => {
        if (!totalMilestones) {
          return;
        }
        getProject({
          variables: {
            projectId: data?.projectId,
            isItProduct: isItProduct
          }
        });
      }}
    >
      <div className="progressBar flex actr w100 pr40">
        {totalMilestones ? (
          <ProgressBar
            progress={calculateProgress(completedMilestones, totalMilestones)}
            size="small"
            customColor="#009f36"
          />
        ) : (
          ''
        )}
        {totalMilestones ? (
          <div className="ml8 font-weight-500">
            {`${completedMilestones}/${totalMilestones}`}
          </div>
        ) : (
          <DisabledTag>NA</DisabledTag>
        )}
      </div>
    </Tippy>
  );
}

export default MilestoneCell;
